import { useState, useRef, useImperativeHandle, forwardRef, useEffect, useMemo, useCallback } from "react";
import { Table, Radio } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import * as XLSX from "xlsx";
import ServiceExcelSale from "./service-excel-sale";
import "./service-excel.css";

dayjs.extend(customParseFormat);

const ServiceExcelSales = forwardRef((props, ref) => {
  const { onSalesChange, clientsRef, productsRef } = props || {};
  const [sales, setSales] = useState([]);
  const [validationErrors, setValidationErrors] = useState({}); // { "recordKey-fieldName": "error" | "warning" }
  const [editingRecord, setEditingRecord] = useState(null); // Запись, которая редактируется
  const [filterType, setFilterType] = useState("all"); // "all" | "errors" | "warnings"
  const salesRef = useRef(null);

  // Маппинг колонок Excel на поля таблиц
  const salesColumnMap = {
    "Внешний ID": "externalId",
    "Дата продажи": "saleDate",
    "Дата активации": "activationDate",
    "Дата окончания": "finishDate",
    "Сумма": "amount",
    "Оплата": "payment",
    "Стоимость": "cost",
    "Скидка": "discount",
    "Скидка сумма": "discountAmount",
    "Комментарий": "comment",
    "Внешний ID клиента": "clientExternalId",
    "Внешний ID абонемента": "abonementExternalId"
  };

  // Валидация для таблицы Абонементы клиентов
  const validateSaleField = (fieldName, value, record, allSales, allClients, allProducts) => {
    const isEmpty = value === null || value === undefined || value === "";

    switch (fieldName) {
      case "externalId":
        if (isEmpty) return "error"; // Критическая ошибка
        // Проверка на дубли
        const duplicateExternalId = allSales.filter(
          (a) => a.key !== record.key && a.externalId === value
        );
        if (duplicateExternalId.length > 0) return "error"; // Критическая ошибка
        return null;

      case "saleDate":
        if (isEmpty) return "error"; // Критическая ошибка
        return null;

      case "activationDate":
        if (isEmpty) return "warning"; // Критическая ошибка
        return null;
      
      case "finishDate":
        if (isEmpty) return "warning"; // Критическая ошибка
        return null;

      case "amount":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && value <= 0) return "warning"; // Предупреждение
        return null;

      case "payment":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && value < 0) return "warning"; // Предупреждение
        if (value > record.amount) return "warning"; // Предупреждение - оплата не может быть больше суммы
        return null;

      case "cost":
        if (isEmpty) return "error"; // Критическая ошибка
        if (value !== null && value !== undefined && value <= 0) return "error"; // Критическая ошибка
        return null;

      case "discount":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && value < 0) return "warning"; // Предупреждение
        return null;

      case "discountAmount":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && value < 0) return "warning"; // Предупреждение
        return null;

      case "comment":
        // Нет правил валидации
        return null;

      case "clientExternalId":
        if (isEmpty) return "error"; // Критическая ошибка
        // Проверка наличия значения в таблице Клиенты (сравнение как числа и строки)
        const clientExists = allClients && allClients.some((c) => {
          const clientId = c.externalId;
          return clientId === value || String(clientId) === String(value) || Number(clientId) === Number(value);
        });
        if (!clientExists) return "error"; // Критическая ошибка
        return null;

      case "abonementExternalId":
        if (isEmpty) return "error"; // Критическая ошибка
        // Проверка наличия значения в таблице Номенклатура (сравнение как числа и строки)
        const productExists = allProducts && allProducts.some((n) => {
          const nomId = n.externalId;
          return nomId === value || String(nomId) === String(value) || Number(nomId) === Number(value);
        });
        if (!productExists) return "error"; // Критическая ошибка
        return null;

      default:
        return null;
    }
  };

  // Функция для валидации всех полей записи
  const validateRecord = (record, allSales, allClients, allProducts) => {
    const errors = {};
    const fieldsToValidate = ["externalId", "saleDate", "activationDate", "finishDate", "amount", "payment", "cost", "discount", "discountAmount", "comment", "clientExternalId", "abonementExternalId"];
    
    fieldsToValidate.forEach(fieldName => {
      const validation = validateSaleField(fieldName, record[fieldName], record, allSales, allClients, allProducts);
      if (validation) {
        errors[`${record.key}-${fieldName}`] = validation;
      }
    });
    
    return errors;
  };

  // Функция для валидации одного поля
  const validateField = (fieldName, record, allSales, allClients, allProducts) => {
    const validation = validateSaleField(fieldName, record[fieldName], record, allSales, allClients, allProducts);
    const errorKey = `${record.key}-${fieldName}`;
    
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      if (validation) {
        newErrors[errorKey] = validation;
      } else {
        delete newErrors[errorKey];
      }
      return newErrors;
    });
  };

  const getCellClassName = useCallback((fieldName, record) => {
    const errorKey = `${record.key}-${fieldName}`;
    const validation = validationErrors[errorKey];
    
    let className = "service-excel-cell";
    if (validation === "error") {
      className += " service-excel-cell-error";
    } else if (validation === "warning") {
      className += " service-excel-cell-warning";
    }
    return className;
  }, [validationErrors]);

  const handleRowClick = useCallback((record) => {
    setEditingRecord({ ...record });
  }, []);

  const handleSave = (updatedRecord) => {
    setSales(prev => {
      // Проверяем, существует ли запись в массиве
      const existingIndex = prev.findIndex(item => item.key === updatedRecord.key);
      let newSales;
      
      if (existingIndex >= 0) {
        // Обновляем существующую запись
        newSales = prev.map(item => 
          item.key === updatedRecord.key ? updatedRecord : item
        );
      } else {
        // Добавляем новую запись
        newSales = [...prev, updatedRecord];
      }
      
      // Валидация после сохранения
      const allClients = clientsRef?.current?.getClients() || [];
      const allProducts = productsRef?.current?.getProducts() || [];
      const recordErrors = validateRecord(updatedRecord, newSales, allClients, allProducts);
      
      setValidationErrors(validationErrorsPrev => {
        const filtered = Object.keys(validationErrorsPrev)
          .filter(key => !key.startsWith(`${updatedRecord.key}-`))
          .reduce((obj, key) => {
            obj[key] = validationErrorsPrev[key];
            return obj;
          }, {});
        return { ...filtered, ...recordErrors };
      });
      
      if (onSalesChange) onSalesChange(newSales.length);
      return newSales;
    });
    setEditingRecord(null);
  };

  const handleCancel = () => {
    setEditingRecord(null);
  };


  const addRow = () => {
    const newKey = Date.now();
    const newRecord = {
      key: newKey,
      externalId: "",
      saleDate: null,
      amount: null,
      payment: null,
      cost: null,
      discount: null,
      discountAmount: null,
      comment: "",
      clientExternalId: "",
      abonementExternalId: ""
    };
    setEditingRecord(newRecord);
  };

  const deleteRow = useCallback((record) => {
    setSales(prev => {
      const newSales = prev.filter(item => item.key !== record.key);
      if (onSalesChange) onSalesChange(newSales.length);
      return newSales;
    });
    // Удаляем все ключи валидации для удаленной записи
    setValidationErrors(prev => {
      const filtered = Object.keys(prev)
        .filter(key => !key.startsWith(`${record.key}-`))
        .reduce((obj, key) => {
          obj[key] = prev[key];
          return obj;
        }, {});
      return filtered;
    });
  }, [onSalesChange]);

  const clearSales = () => {
    setSales([]);
    setValidationErrors({});
    if (onSalesChange) onSalesChange(0);
  };

  const parseSheetData = (worksheet) => {
    if (!worksheet) return [];
    
    // Используем raw: false для правильной обработки дат
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1, 
      defval: "",
      raw: false,
      dateNF: "dd.mm.yyyy"
    });
    if (jsonData.length < 2) return []; // Нет заголовков или данных

    const headers = jsonData[0].map(h => String(h || "").trim());
    const rows = jsonData.slice(1);

    // Определяем поля, которые должны быть датами
    const dateFields = ["saleDate", "activationDate", "finishDate"];

    return rows.map((row, index) => {
      // Проверяем первую колонку - если пустая, пропускаем запись
      const firstColumnValue = row[0];
      if (firstColumnValue === "" || firstColumnValue === null || firstColumnValue === undefined) {
        return null; // Возвращаем null для фильтрации
      }
      
      const record = { key: `sale-${Date.now()}-${index}-${Math.random()}` };
      headers.forEach((header, colIndex) => {
        // Ищем поле с учетом возможных различий в регистре и пробелах
        let fieldName = salesColumnMap[header];
        if (!fieldName) {
          const matchedKey = Object.keys(salesColumnMap).find(key => 
            key.toLowerCase().trim() === header.toLowerCase().trim()
          );
          if (matchedKey) {
            fieldName = salesColumnMap[matchedKey];
          }
        }
        
        if (fieldName) {
          let value = row[colIndex];
          
          // Обработка пустых значений
          if (value === "" || value === null || value === undefined) {
            value = null;
          } 
          // Обработка дат
          else if (dateFields.includes(fieldName)) {
            if (typeof value === "number") {
              // Excel дата как число дней с 1900-01-01
              const excelEpoch = new Date(1899, 11, 30);
              const date = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
              value = date.toISOString();
            } else if (typeof value === "string") {
              // Убираем пробелы и пробуем распарсить строку как дату
              const trimmedValue = value.trim();
              
              let parsed = null;
              
              // 1. Сначала пробуем формат dd.MM.yyyy (основной формат)
              parsed = dayjs(trimmedValue, ["DD.MM.YYYY","YYYY-MM-DD", "DD/MM/YYYY", "D.M.YYYY", "DD.M.YYYY", "D.MM.YYYY"], true); // strict mode
              
              // 2. Если не получилось, пробуем формат d/M/yy или dd/MM/yy (с двухзначным годом)
              if (!parsed.isValid()) {
                const slashDateMatch = trimmedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
                if (slashDateMatch) {
                  const day = parseInt(slashDateMatch[1], 10);
                  const month = parseInt(slashDateMatch[2], 10);
                  const year2digits = parseInt(slashDateMatch[3], 10);
                  // Конвертируем двухзначный год в четырехзначный: 00-99 -> 2000-2099
                  const year4digits = 2000 + year2digits;
                  parsed = dayjs(`${year4digits}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`, "YYYY-MM-DD", true);
                }
              }
              
              // 3. Если не получилось, пробуем другие форматы
              if (!parsed.isValid()) {
                parsed = dayjs(trimmedValue, ["DD.MM.YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY", "M/D/YYYY", "M/D/YY", "D.M.YYYY", "DD.M.YYYY", "D.MM.YYYY"], true);
              }
              
              if (parsed.isValid()) {
                value = parsed.toISOString();
              } else {
                value = null;
              }
            }
          }
          // Обработка чисел
          else if (typeof value === "number") {
            value = value;
          } 
          // Обработка строковых значений
          else {
            value = String(value).trim();
            
            // Пытаемся преобразовать строку в число, если поле числовое
            const numericFields = [
              "amount", "payment", "cost", "discount", "discountAmount",
              "clientExternalId", "abonementExternalId"
            ];
            if (numericFields.includes(fieldName) && value !== "") {
              const numValue = parseFloat(value.replace(",", "."));
              value = isNaN(numValue) ? null : numValue;
            }
          }
          
          record[fieldName] = value;
        }
      });
      return record;
    }).filter(record => {
      // Фильтруем null записи (где первая колонка была пустая)
      if (record === null) return false;
      // Фильтруем пустые строки - проверяем, есть ли хотя бы одно непустое значение
      const values = Object.values(record).filter(key => key !== "key");
      return values.some(value => value !== null && value !== "" && value !== undefined);
    });
  };

  const loadFromExcel = (worksheet) => {
    const salesData = parseSheetData(worksheet);
    // Создаем новый массив для гарантии обновления React
    setSales([...salesData]);
    
    // Валидация после загрузки
    setTimeout(() => {
      const errors = {};
      const allClients = clientsRef?.current?.getClients() || [];
      const allProducts = productsRef?.current?.getProducts() || [];
      salesData.forEach(record => {
        const recordErrors = validateRecord(record, salesData, allClients, allProducts);
        Object.assign(errors, recordErrors);
      });
      setValidationErrors(errors);
      
      if (onSalesChange) onSalesChange(salesData.length);
    }, 0);
    
    return salesData.length;
  };

  // Синхронизация количества продаж с родительским компонентом
  useEffect(() => {
    if (onSalesChange) {
      onSalesChange(sales.length);
    }
  }, [sales, onSalesChange]);

  // Проверка наличия критических ошибок
  const hasCriticalErrors = () => {
    const allClients = clientsRef?.current?.getClients() || [];
    const allProducts = productsRef?.current?.getProducts() || [];
    for (const sale of sales) {
      if (validateSaleField("externalId", sale.externalId, sale, sales, allClients, allProducts) === "error") return true;
      if (validateSaleField("saleDate", sale.saleDate, sale, sales, allClients, allProducts) === "error") return true;
      if (validateSaleField("cost", sale.cost, sale, sales, allClients, allProducts) === "error") return true;
      if (validateSaleField("clientExternalId", sale.clientExternalId, sale, sales, allClients, allProducts) === "error") return true;
      if (validateSaleField("abonementExternalId", sale.abonementExternalId, sale, sales, allClients, allProducts) === "error") return true;
    }
    return false;
  };

  // Получение данных для сохранения
  const getSalesData = () => {
    const formatDate = (dateStr) => {
      if (!dateStr) return null;
      const date = dayjs(dateStr);
      return date.isValid() ? date.format("YYYY-MM-DD") : null;
    };

    return sales.map(sale => ({
      Id: parseInt(sale.externalId) || 0,
      CompanyId: 0,
      CreatedOn: formatDate(sale.saleDate) || dayjs().format("YYYY-MM-DD"),
      DocDate: formatDate(sale.saleDate) || dayjs().format("YYYY-MM-DD"),
      Activation: formatDate(sale.activationDate) || dayjs().format("YYYY-MM-DD"),
      Finish: formatDate(sale.finishDate) || dayjs().format("YYYY-MM-DD"),
      Syma: sale.amount !== null && sale.amount !== undefined ? parseFloat(sale.amount) : 0,
      Price: sale.cost !== null && sale.cost !== undefined ? parseFloat(sale.cost) : 0,
      Paid: sale.payment !== null && sale.payment !== undefined ? parseFloat(sale.payment) : 0,
      Comment: sale.comment || "",
      ClientId: parseInt(sale.clientExternalId) || 0,
      ProductId: parseInt(sale.abonementExternalId) || 0,
      Status: 1
    }));
  };

  // Проверка наличия ошибок у записи
  const hasRecordError = useCallback((record) => {
    const fieldsToValidate = ["externalId", "saleDate", "activationDate", "finishDate", "amount", "payment", "cost", "discount", "discountAmount", "comment", "clientExternalId", "abonementExternalId"];
    const recordKey = String(record.key);
    
    for (const field of fieldsToValidate) {
      const errorKey = `${recordKey}-${field}`;
      if (validationErrors[errorKey] === "error") {
        return true;
      }
    }
    return false;
  }, [validationErrors]);

  // Проверка наличия предупреждений у записи
  const hasRecordWarning = useCallback((record) => {
    const fieldsToValidate = ["externalId", "saleDate", "activationDate", "finishDate", "amount", "payment", "cost", "discount", "discountAmount", "comment", "clientExternalId", "abonementExternalId"];
    const recordKey = String(record.key);
    
    for (const field of fieldsToValidate) {
      const errorKey = `${recordKey}-${field}`;
      if (validationErrors[errorKey] === "warning") {
        return true;
      }
    }
    return false;
  }, [validationErrors]);

  // Подсчет записей с ошибками и предупреждениями
  const getValidationCounts = () => {
    const errorKeys = new Set();
    const warningKeys = new Set();
    
    // Получаем множество существующих ключей записей (как строки для сравнения)
    const existingKeys = new Set(sales.map(s => String(s.key)));
    
    // Получаем список всех полей для валидации
    const fieldsToValidate = ["externalId", "saleDate", "activationDate", "finishDate", "amount", "payment", "cost", "discount", "discountAmount", "comment", "clientExternalId", "abonementExternalId"];
    
    // Сначала собираем все ошибки и предупреждения по записям
    Object.entries(validationErrors).forEach(([key, type]) => {
      // Извлекаем ключ записи - находим последнее поле из списка полей в ключе
      let recordKey = null;
      for (const field of fieldsToValidate) {
        if (key.endsWith(`-${field}`)) {
          recordKey = key.slice(0, -(field.length + 1)); // Убираем "-fieldName" с конца
          break;
        }
      }
      
      // Если не нашли через поля, используем старый метод как fallback
      if (!recordKey) {
        const parts = key.split('-');
        if (parts.length > 1) {
          // Берем все части кроме последней (которая является fieldName)
          recordKey = parts.slice(0, -1).join('-');
        } else {
          recordKey = key;
        }
      }
      
      // Учитываем только ошибки для существующих записей
      if (recordKey && existingKeys.has(recordKey)) {
        if (type === "error") {
          errorKeys.add(recordKey);
        } else if (type === "warning") {
          warningKeys.add(recordKey);
        }
      }
    });
    
    return {
      errors: errorKeys.size,
      warnings: warningKeys.size
    };
  };

  // Предоставляем методы через ref
  useImperativeHandle(ref, () => ({
    clearSales,
    loadFromExcel,
    hasCriticalErrors,
    getSalesData,
    getSales: () => sales,
    addRow,
    getValidationCounts
  }));

  const getSalesColumns = useMemo(() => [
    {
      title: "",
      width: 60,
      align: "center",
      render: (_, record) => (
        <img
          src="/images/trashcan.svg"
          alt="delete"
          onClick={(e) => {
            e.stopPropagation();
            deleteRow(record);
          }}
          className="img-edit-btn service-excel-action-icon"
        />
      ),
    },
    {
      title: "Внешний ID",
      dataIndex: "externalId",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("externalId", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Дата продажи",
      dataIndex: "saleDate",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("saleDate", record);
        let displayValue = "";
        if (record.saleDate) {
          const parsed = dayjs(record.saleDate);
          if (parsed.isValid()) {
            displayValue = parsed.format("DD.MM.YYYY");
          }
        }
        return <div className={className}>{displayValue}</div>;
      },
    },
    {
      title: "Дата активации",
      dataIndex: "activationDate",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("activationDate", record);
        let displayValue = "";
        if (record.activationDate) {
          const parsed = dayjs(record.activationDate);
          if (parsed.isValid()) {
            displayValue = parsed.format("DD.MM.YYYY");
          }
        }
        return <div className={className}>{displayValue}</div>;
      },
    },
    {
      title: "Дата окончания",
      dataIndex: "finishDate",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("finishDate", record);
        let displayValue = "";
        if (record.finishDate) {
          const parsed = dayjs(record.finishDate);
          if (parsed.isValid()) {
            displayValue = parsed.format("DD.MM.YYYY");
          }
        }
        return <div className={className}>{displayValue}</div>;
      },
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("amount", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Оплата",
      dataIndex: "payment",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("payment", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Стоимость",
      dataIndex: "cost",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("cost", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Скидка",
      dataIndex: "discount",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("discount", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Скидка сумма",
      dataIndex: "discountAmount",
      width: 130,
      render: (text, record) => {
        const className = getCellClassName("discountAmount", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Комментарий",
      dataIndex: "comment",
      width: 200,
      render: (text, record) => {
        const className = getCellClassName("comment", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Внешний ID клиента",
      dataIndex: "clientExternalId",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("clientExternalId", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Внешний ID абонемента",
      dataIndex: "abonementExternalId",
      width: 180,
      render: (text, record) => {
        const className = getCellClassName("abonementExternalId", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
  ], [deleteRow, getCellClassName]);

  const getTableHeight = (ref) => {
    if (!ref.current) return 400;
    const headerSize = ref.current.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
    return ref.current ? (document.documentElement.offsetHeight - headerSize - ref.current.getBoundingClientRect().top - window.scrollY - 150) : 400;
  };

  // Компоненты для таблиц с убранным padding в body ячейках
  const tableComponents = {
    body: {
      cell: (props) => {
        const { children, ...restProps } = props;
        return (
          <td {...restProps} style={{ ...restProps.style, padding: 0 }}>
            {children}
          </td>
        );
      },
    },
  };

  // Фильтрация данных в зависимости от выбранного фильтра
  const filteredSales = useMemo(() => {
    if (filterType === "all") {
      return sales;
    } else if (filterType === "errors") {
      return sales.filter(sale => hasRecordError(sale));
    } else if (filterType === "warnings") {
      return sales.filter(sale => hasRecordWarning(sale));
    }
    return sales;
  }, [sales, filterType, hasRecordError, hasRecordWarning]);

  // Получаем счетчики для отображения
  const validationCounts = getValidationCounts();

  return (
    <>
      <div ref={salesRef}>
        <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
          <Radio.Group 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="all">
              Всего ({sales.length})
            </Radio.Button>
            <Radio.Button value="errors">
              Ошибок ({validationCounts.errors})
            </Radio.Button>
            <Radio.Button value="warnings">
              Предупреждений ({validationCounts.warnings})
            </Radio.Button>
          </Radio.Group>
        </div>
        <Table
          dataSource={filteredSales}
          columns={getSalesColumns}
          rowKey="key"
          size="small"
          pagination={false}
          scroll={{ x: "max-content", y: getTableHeight(salesRef) }}
          bordered
          components={tableComponents}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: "pointer" }
          })}
        />
      </div>
      {editingRecord && (
        <ServiceExcelSale
          record={editingRecord}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
    </>
  );
});

ServiceExcelSales.displayName = "ServiceExcelSales";

export default ServiceExcelSales;

